import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Send, Clock, XCircle, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_LABEL = {
  auto_evaluation: { subject: "L'évaluation automatique", of: "d'évaluation automatique" },
  retroaction: { subject: 'La rétroaction', of: 'de rétroaction' },
};

export const EvaluationRequestPanel = ({ requestType, sessionType, sessionId, studentInfo, isAdmin, onAdminGenerate, generating }) => {
  const [requests, setRequests] = useState([]);
  const [comment, setComment] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/evaluation-requests`, { params: { session_id: sessionId, request_type: requestType } });
      setRequests(resp.data || []);
    } catch {
      // silent — panel still shows the request form
    } finally {
      setLoaded(true);
    }
  }, [sessionId, requestType]);

  useEffect(() => { load(); }, [load]);

  const latest = requests[0];
  const label = TYPE_LABEL[requestType] || TYPE_LABEL.auto_evaluation;

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/evaluation-requests`, {
        request_type: requestType,
        session_type: sessionType,
        session_id: sessionId,
        student_number: studentInfo?.student_number || '',
        student_name: studentInfo ? `${studentInfo.first_name} ${studentInfo.last_name}` : '',
        comment,
      });
      toast.success('Demande envoyée à votre instructeur');
      setComment('');
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de l'envoi de la demande");
    } finally {
      setSending(false);
    }
  };

  if (!loaded) {
    return <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="w-full max-w-lg mx-auto space-y-4" data-testid="evaluation-request-panel">
      {latest?.status === 'pending' ? (
        <div className="p-4 rounded-xl border-2 border-amber-300 bg-amber-50 text-center" data-testid="request-pending-panel">
          <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-amber-800">Demande envoyée — en attente d'approbation</p>
          <p className="text-xs text-amber-700 mt-1">
            Votre demande {label.of} du {new Date(latest.created_at).toLocaleString('fr-CA')} a été transmise à votre instructeur.
          </p>
          {latest.comment && <p className="text-xs text-amber-600 mt-2 italic">« {latest.comment} »</p>}
        </div>
      ) : latest?.status === 'refused' && !showForm ? (
        <div className="p-4 rounded-xl border-2 border-rose-300 bg-rose-50 text-center" data-testid="request-refused-panel">
          <XCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-rose-800">Demande refusée par l'instructeur</p>
          {latest.refusal_reason && <p className="text-xs text-rose-700 mt-1">Raison : {latest.refusal_reason}</p>}
          <button
            onClick={() => setShowForm(true)}
            className="mt-3 px-4 py-2 rounded-lg text-white text-sm font-medium"
            style={{ backgroundColor: '#002768' }}
            data-testid="request-again-btn"
          >
            Faire une nouvelle demande
          </button>
        </div>
      ) : (
        <div className="p-4 rounded-xl border bg-white space-y-3" data-testid="request-form-panel">
          <p className="text-sm text-gray-700">
            {label.subject} est générée par l'IA sur approbation de votre instructeur. Envoyez-lui votre demande :
          </p>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Message pour l'instructeur (optionnel) — ex: « J'aimerais un retour sur ma gestion de la P1 »"
            rows={3}
            className="w-full px-3 py-2 text-sm border rounded-lg resize-none"
            data-testid="request-comment-input"
          />
          <button
            onClick={submit}
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: '#002768' }}
            data-testid="send-request-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer la demande à l'instructeur
          </button>
        </div>
      )}

      {isAdmin && onAdminGenerate && (
        <button
          onClick={onAdminGenerate}
          disabled={generating}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold border-2 disabled:opacity-50"
          style={{ borderColor: '#7C3AED', color: '#7C3AED', backgroundColor: '#F5F3FF' }}
          data-testid="admin-generate-btn"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Générer maintenant (admin)
        </button>
      )}
    </div>
  );
};

export default EvaluationRequestPanel;
